/**
 * Iperf "tests": one Start (or one scheduled run) creates one run per CAP
 * tree. Runs carry a shared test_id; runs from before it existed are
 * grouped by base name (the part before " — <cap>"), creator and start
 * time (created within FALLBACK_WINDOW_MS of the test's first run).
 */

const FALLBACK_WINDOW_MS = 10000;

/**
 * A retry response: the run itself when it starts now, or
 * { queued: true, run } (HTTP 202) when another iperf run is active and
 * the retry waits its turn.
 */
export function unwrapIperfRetryResponse(resp) {
  if (resp && typeof resp === 'object' && resp.run && typeof resp.run === 'object') {
    return { run: resp.run, queued: resp.queued === true };
  }
  return { run: resp || null, queued: false };
}

/** Test name without the per-CAP suffix ("Nightly — a85b…" → "Nightly"). */
export function iperfTestBaseName(name) {
  const n = String(name || '').trim();
  const i = n.lastIndexOf(' — ');
  return i > 0 ? n.slice(0, i) : n;
}

const ts = (run) => {
  const t = Date.parse(run?.created_at || '');
  return Number.isFinite(t) ? t : 0;
};

// Most active status first: what the test is doing as a whole.
const STATUS_ORDER = ['running', 'queued', 'pending', 'failed', 'timed_out', 'cancelled', 'completed'];

/** Combined status, per-status counts and progress of a test's runs. */
export function summarizeIperfTest(runs) {
  const counts = {};
  let completedTests = 0;
  let totalTests = 0;
  let healthy = 0;
  let degraded = 0;
  (runs || []).forEach((r) => {
    counts[r.status] = (counts[r.status] || 0) + 1;
    completedTests += r.summary?.completed || 0;
    totalTests += r.summary?.total_tests || 0;
    healthy += r.summary?.healthy_paths || 0;
    degraded += r.summary?.degraded_paths || 0;
  });
  const status = STATUS_ORDER.find((s) => counts[s]) || Object.keys(counts)[0] || 'pending';
  const active = (counts.running || 0) + (counts.queued || 0) + (counts.pending || 0);
  return { status, counts, active, completedTests, totalTests, healthy, degraded };
}

/**
 * Runs grouped into tests, newest test first; runs inside a test in
 * creation order. Each: { id, name, createdAt, createdBy, runs, ...summary }.
 */
export function groupIperfTests(runs) {
  const sorted = [...(runs || [])].filter(Boolean).sort((a, b) => ts(b) - ts(a));
  const groups = [];
  const byTestId = new Map();
  sorted.forEach((run) => {
    if (run.test_id) {
      let g = byTestId.get(run.test_id);
      if (!g) {
        g = { id: `test:${run.test_id}`, runs: [] };
        byTestId.set(run.test_id, g);
        groups.push(g);
      }
      g.runs.push(run);
      return;
    }
    const base = iperfTestBaseName(run.name);
    const g = groups.find((x) => x.fallback
      && x.base === base
      && x.createdBy === (run.created_by || '')
      && Math.abs(x.anchor - ts(run)) <= FALLBACK_WINDOW_MS);
    if (g) {
      g.runs.push(run);
      return;
    }
    groups.push({
      id: `run:${run.id}`, fallback: true, base, createdBy: run.created_by || '', anchor: ts(run), runs: [run],
    });
  });
  return groups.map((g) => {
    const runsAsc = [...g.runs].sort((a, b) => ts(a) - ts(b) || String(a.name).localeCompare(String(b.name)));
    const first = runsAsc[0];
    return {
      id: g.id,
      name: iperfTestBaseName(first?.name) || '',
      createdAt: first?.created_at,
      createdBy: first?.created_by || '',
      runs: runsAsc,
      ...summarizeIperfTest(runsAsc),
    };
  }).sort((a, b) => ts({ created_at: b.createdAt }) - ts({ created_at: a.createdAt }));
}
